import {
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { LayerStoreService } from '../../../../mapbox/services/layer-store.service';
import { ColorscaleService } from '../../../../mapbox/visualization/colorscale/colorscale.service';
import { UserAccessService } from '../../../../../user/access/user-access.service';
import { ColorScaleComponent } from '../../../../mapbox/visualization/colorscale/color-scale.component';
import { FeatureTooltipComponent } from '../shared/feature-tooltip/feature-tooltip.component';
import { IsFeatureAccessiblePipe } from '../shared/is-feature-accessible.pipe';
import { LayerNameFormatPipe } from '../../../../common-colorscale/layer-name-format.pipe';
import {
  FeatureScope,
  MapColorControlBlock,
  MenuGroupDisclaimer,
} from '../menu-group/menu-group.component';
import { MapModalData } from '../../../../../user/map-redirect-modal/map-redirect-modal.component';
import { UserGuideService } from '../../../../user-guide/user-guide.service';
import {
  COMBINED_HWW_CONST,
  HEALTH_INDEX_CONST,
  WEALTH_INDEX_CONST,
  WISE_INDEX_CONST,
} from '../../../../../shared/types/feature-data-type';

/**
 * Long enough to clear the 200ms debounce on `activeLayer` that the linked
 * toggles hang off, plus the render they cause.
 */
const LAYOUT_SETTLE_MS = 300;

export interface LayerRow {
  feature: MapColorControlBlock;
  /** Empty for every row that is not one of the four area indices. */
  iconPath: string;
  blurb: string;
  isHero: boolean;
}

/**
 * The four area indices are the only rows that carry a glyph and a line of
 * what they measure: they are the menu's summary block, and the popup grades a
 * clicked cell against them. Every other indicator is a plain row - a name and
 * the colour scale when it is the one painting the map.
 *
 * One path string per glyph rather than an icon component or four more
 * registered assets: they are 20px line drawings on a 24px grid, and every
 * subpath in them takes the same stroke.
 */
const INDEX_ROW_COPY: {
  [featureConst: string]: { path: string; blurb: string };
} = {
  [COMBINED_HWW_CONST]: {
    path: 'M12 3.2 14.5 8l5.3.8-3.8 3.7.9 5.3-4.9-2.6-4.9 2.6.9-5.3L4.2 8.8 9.5 8 12 3.2Z',
    blurb: 'Health, income and education combined',
  },
  [HEALTH_INDEX_CONST]: {
    path: 'M20.4 6.9a4.7 4.7 0 0 0-7.1-.6l-1.3 1.3-1.3-1.3a4.7 4.7 0 0 0-7.1.6c-1.2 1.8-.9 4.3.7 5.9l7.7 7.6 7.7-7.6c1.6-1.6 1.9-4.1.7-5.9Z',
    blurb: 'Life expectancy, illness, care access',
  },
  [WEALTH_INDEX_CONST]: {
    path:
      'M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z M12 6.8v10.4 ' +
      'M14.6 9.3c-.6-.7-1.6-1-2.6-1-1.5 0-2.6.8-2.6 2s1 1.7 2.6 2c1.7.3 2.7.8 2.7 2s-1.2 2-2.7 2c-1.1 0-2.1-.4-2.7-1.1',
    blurb: 'Income, jobs, homeownership',
  },
  [WISE_INDEX_CONST]: {
    path:
      'M2.8 8.9 12 4.5l9.2 4.4-9.2 4.4-9.2-4.4Z ' +
      'M6.6 10.7v4.6c0 1.6 2.4 2.9 5.4 2.9s5.4-1.3 5.4-2.9v-4.6',
    blurb: 'Attainment and school quality',
  },
};

/**
 * Every map-colour row in the right menu: one list, one look, for all eleven
 * groups.
 *
 * Replaces a per-row `app-custom-radio` (a component, a ripple and a hidden
 * input each, ~60 of them live while the menu is open) with a plain row that
 * is itself the control. The map is the selected state - the row only has to
 * say which one is painting it, which the accent rail, the blue name and the
 * nested colour scale do between them.
 */
@Component({
  selector: 'app-layer-rows',
  templateUrl: './layer-rows.component.html',
  styleUrls: ['./layer-rows.component.scss'],
  imports: [
    AsyncPipe,
    ColorScaleComponent,
    FeatureTooltipComponent,
    IsFeatureAccessiblePipe,
    LayerNameFormatPipe,
  ],
  // Default change detection on purpose: the nested colour scale reads mutable
  // fields off ColorscaleService that emit nothing when they are replaced (see
  // the note on ColorScaleComponent), so OnPush here would freeze the scale
  // under the active row.
})
export class LayerRowsComponent implements OnChanges, OnDestroy {
  @Input() features: MapColorControlBlock[] = [];
  @Input() isPaidPlan: boolean | null = null;
  /** Null for the ten groups whose layers are not scoped to US / state. */
  @Input() selectedScope: FeatureScope | null = null;
  /** False on the state level, where "vs state" would compare a state to itself. */
  @Input() showScope = false;
  @Input() disclaimer: MenuGroupDisclaimer | null = null;

  @Output() scopeChange = new EventEmitter<FeatureScope>();
  @Output() layerSelect = new EventEmitter<string>();
  @Output() lockedFeatureClick = new EventEmitter<MapColorControlBlock>();
  @Output() openTooltipModal = new EventEmitter<MapModalData>();

  public rows: LayerRow[] = [];

  public readonly FeatureScope = FeatureScope;

  public readonly guideService = inject(UserGuideService);
  public readonly layerStore = inject(LayerStoreService);
  public readonly colorScaleService = inject(ColorscaleService);
  public readonly accessService = inject(UserAccessService);

  private readonly zone = inject(NgZone);
  private reanchorTimer?: ReturnType<typeof setTimeout>;

  ngOnDestroy(): void {
    clearTimeout(this.reanchorTimer);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['features']) return;

    // The combined index is last in the menu config but reads as the headline,
    // so it is hoisted here rather than reordered in the config the map, the
    // filters and the popup all share.
    const ordered = [
      ...this.features.filter((f) => f.featureConst === COMBINED_HWW_CONST),
      ...this.features.filter((f) => f.featureConst !== COMBINED_HWW_CONST),
    ];

    this.rows = ordered.map((feature) => ({
      feature,
      iconPath: INDEX_ROW_COPY[feature.featureConst]?.path ?? '',
      blurb: INDEX_ROW_COPY[feature.featureConst]?.blurb ?? '',
      isHero: feature.featureConst === COMBINED_HWW_CONST,
    }));
  }

  /**
   * The layer the map paints for this row: the indicator plus the active scope,
   * where the group has one. The scope survives the switch being hidden - on
   * the state level the parent has already forced it back to `BY_COUNTRY`.
   */
  public layerOf(row: LayerRow): string {
    return row.feature.featureConst + (this.selectedScope ?? '');
  }

  public isVisible(row: LayerRow, isAccessible: boolean): boolean {
    return (
      (!this.isPaidPlan && !row.feature.isAdminOnly) ||
      (!!this.isPaidPlan && isAccessible)
    );
  }

  public isDisabledOnLevel(row: LayerRow, activeLevel: string | null): boolean {
    const levels = row.feature.accessibleOnLevels;
    return !!levels && !levels.includes(activeLevel!);
  }

  public onRowClick(
    row: LayerRow,
    isAccessible: boolean,
    isDisabledOnLevel: boolean,
    element: HTMLElement
  ): void {
    if (isDisabledOnLevel) return;

    if (!isAccessible) {
      this.lockedFeatureClick.emit(row.feature);
      return;
    }

    this.layerSelect.emit(this.layerOf(row));
    this.keepRowInView(element);
  }

  /**
   * Picking a layer can unfurl a different group *above* this one: Life
   * expectancy switches on the population-by-years chart, which opens the
   * Population group (see `LayersDataService.linkedToggles`), and the row the
   * visitor just clicked is pushed down the panel or off it entirely.
   *
   * Those linked toggles run on a 200ms debounce off `activeLayer`, so the
   * correction cannot wait for the next frame - it waits for that layout, then
   * gives back exactly the distance the row moved, leaving it under the cursor.
   */
  private keepRowInView(element: HTMLElement): void {
    const scroller = this.scrollParentOf(element);
    if (!scroller) return;

    const before = element.getBoundingClientRect().top;

    this.zone.runOutsideAngular(() => {
      clearTimeout(this.reanchorTimer);
      this.reanchorTimer = setTimeout(() => {
        const shift = element.getBoundingClientRect().top - before;
        if (Math.abs(shift) > 1) scroller.scrollTop += shift;
      }, LAYOUT_SETTLE_MS);
    });
  }

  private scrollParentOf(element: HTMLElement): HTMLElement | null {
    for (let node = element.parentElement; node; node = node.parentElement) {
      const overflowY = getComputedStyle(node).overflowY;
      const scrolls = overflowY === 'auto' || overflowY === 'scroll';

      if (scrolls && node.scrollHeight > node.clientHeight) return node;
    }

    return null;
  }

  public onScopeClick(scope: FeatureScope): void {
    if (scope !== this.selectedScope) this.scopeChange.emit(scope);
  }
}
